// Settings → Payments (docs/plans/payment-qr.md): the one QR a business shows at
// checkout. Owners and admins add it by scanning their printed QR or choosing a
// photo of it; the app keeps what the code says, never the picture.
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Field,
  IconTile,
  Input,
  Modal,
  Select,
  SkeletonForm,
  useToast,
} from '@ims/ui'
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  Eye,
  History,
  type IconType,
  ImagePlus,
  Info,
  LockKeyhole,
  QrCode,
  RefreshCw,
  Trash2,
} from '@ims/ui/icons'
import { type ReactNode, useRef, useState } from 'react'
import { errorMessage, useSettings } from '../hooks.ts'
import { dateLabel } from '../lib/format.ts'
import {
  PAYMENT_QR_LABEL_MAX,
  PAYMENT_QR_PAYEE_MAX,
  type PaymentQrMethod,
  paymentQrOf,
  readPaymentQr,
} from '../lib/payment-qr.ts'
import { updateOp } from '../lib/rows.ts'
import { useStaffNames } from '../lib/staff.ts'
import { PaymentQrTile } from '../payment-qr.tsx'
import { ScannerDialog } from '../scanner.tsx'
import { useSession } from '../session.tsx'
import { useCan, useRepo } from '../workspace.tsx'
import { SettingsPart } from './settings-part.tsx'

type SettingsRow = NonNullable<ReturnType<typeof useSettings>>

/** A code that was read (or the saved one, being edited) with the words that go with it. */
interface Draft {
  payload: string
  label: string
  payee: string
  method: PaymentQrMethod
  /** Just read from a camera or a photo, as opposed to the saved code's details. */
  fresh: boolean
}

const METHOD_LABEL: Record<PaymentQrMethod, string> = { wallet: 'Wallet', bank: 'Bank' }

export function PaymentSettings() {
  const settings = useSettings()
  if (!settings) return <SkeletonForm />
  return <PaymentQrSettings settings={settings} />
}

function PaymentQrSettings({ settings }: { settings: SettingsRow }) {
  const canEdit = useCan('admin')
  const repo = useRepo()
  const toast = useToast()
  const { platform } = useSession()
  const names = useStaffNames()
  const qr = paymentQrOf(settings)
  const file = useRef<HTMLInputElement>(null)
  const [scanning, setScanning] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [problem, setProblem] = useState<{ title: string; message: string } | null>(null)
  const [removing, setRemoving] = useState(false)
  const [busy, setBusy] = useState(false)
  const canScan = platform.scanner.available()

  /** What a camera or a photo read: a draft to name, or why it cannot be kept. */
  const take = (text: string) => {
    const read = readPaymentQr(text)
    if (!read.ok) {
      setProblem({ title: 'This is not a payment QR', message: read.message })
      return read
    }
    setProblem(null)
    setDraft({
      payload: read.payload,
      label: read.label || (qr?.label ?? ''),
      payee: read.payee ?? settings.business_name,
      method: settings.payment_qr_method,
      fresh: true,
    })
    return read
  }

  const choose = async (picked: File | undefined) => {
    if (!picked) return
    setBusy(true)
    try {
      const text = await platform.scanner.readImage(picked)
      if (text === null) {
        setProblem({
          title: 'No QR found in this photo',
          message:
            'Try a sharper photo with the whole QR in it, or point the camera at the printed one.',
        })
      } else {
        take(text)
      }
    } catch {
      setProblem({
        title: 'This file could not be opened',
        message: 'Choose a photo or a screenshot of your QR.',
      })
    } finally {
      setBusy(false)
    }
  }

  const commit = async (changes: Record<string, unknown>, message: string) => {
    setBusy(true)
    try {
      await repo.commit([updateOp('tenant_settings', settings, changes)])
      toast.success(message)
      return true
    } catch (error) {
      toast.error(errorMessage(error))
      return false
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    if (!draft) return
    const saved = await commit(
      {
        payment_qr: draft.payload,
        payment_qr_label: draft.label.trim(),
        payment_qr_payee: draft.payee.trim() || null,
        payment_qr_method: draft.method,
      },
      draft.fresh ? 'Payment QR saved' : 'Saved',
    )
    if (saved) setDraft(null)
  }

  const addButtons = (
    <>
      {canScan && (
        <Button
          icon={<Camera className="size-4" aria-hidden />}
          disabled={busy}
          onClick={() => setScanning(true)}
        >
          Scan your QR
        </Button>
      )}
      <Button
        variant={canScan ? 'secondary' : 'primary'}
        icon={<ImagePlus className="size-4" aria-hidden />}
        loading={busy}
        onClick={() => file.current?.click()}
      >
        Choose a photo
      </Button>
    </>
  )
  const setBy = settings.payment_qr_set_by ? names[settings.payment_qr_set_by] : undefined

  return (
    <>
      <Card className="p-0">
        <SettingsPart
          title="Payment QR"
          about="The QR from your bank or wallet. One for the whole business, shown at checkout on every device."
        >
          {qr ? (
            <div className="flex flex-wrap items-start gap-x-5 gap-y-3 sm:col-span-2">
              <PaymentQrTile payload={qr.payload} className="w-32" />
              <div className="min-w-0 flex-1 basis-52">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <b className="text-base tracking-tight" translate="no">
                    {qr.label}
                  </b>
                  <Badge tone="success" className="gap-1">
                    <CheckCircle2 className="size-3.5" aria-hidden />
                    Shown at checkout
                  </Badge>
                </div>
                <dl className="grid gap-1 text-sm">
                  <Line label="Pays to">
                    <span translate="no">{qr.payee ?? '—'}</span>
                  </Line>
                  <Line label="Recorded as">{METHOD_LABEL[qr.method]}</Line>
                  {settings.payment_qr_set_at && (
                    <Line label="Added">
                      {dateLabel(settings.payment_qr_set_at, settings)}
                      {setBy && (
                        <>
                          {' by '}
                          <span translate="no">{setBy}</span>
                        </>
                      )}
                    </Line>
                  )}
                </dl>
                {canEdit ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() =>
                        setDraft({
                          payload: qr.payload,
                          label: settings.payment_qr_label ?? '',
                          payee: settings.payment_qr_payee ?? '',
                          method: qr.method,
                          fresh: false,
                        })
                      }
                    >
                      Edit details
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={busy}
                      icon={<RefreshCw className="size-4" aria-hidden />}
                      onClick={() => (canScan ? setScanning(true) : file.current?.click())}
                    >
                      Replace QR
                    </Button>
                    <Button
                      variant="secondary"
                      className="text-danger!"
                      disabled={busy}
                      icon={<Trash2 className="size-4" aria-hidden />}
                      onClick={() => setRemoving(true)}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Locked />
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2.5 px-2 py-4 text-center sm:col-span-2">
              <IconTile area="sell" size="lg">
                <QrCode className="size-7" />
              </IconTile>
              <b className="text-base tracking-tight">No payment QR yet</b>
              <p className="max-w-xs text-sm text-muted">
                Add the QR your bank or wallet gave you, and the checkout shows it whenever a
                customer pays by QR.
              </p>
              {canEdit ? (
                <div className="mt-1 flex flex-wrap justify-center gap-2">{addButtons}</div>
              ) : (
                <Locked />
              )}
            </div>
          )}
        </SettingsPart>
        <SettingsPart
          title="Who sees it"
          about="The QR decides where the money goes, so only a few people can change it."
        >
          <div className="grid gap-3 sm:col-span-2">
            <Who icon={Eye} title="Everyone who bills sees it at checkout">
              Waiters, staff, managers, admins and owners.
            </Who>
            <Who icon={LockKeyhole} title="Only owners and admins can add, replace or remove it">
              Checked by the server, not only hidden in the app.
            </Who>
            <Who icon={History} title="The latest change is listed in Activity">
              With who made it and when.
            </Who>
          </div>
        </SettingsPart>
        <SettingsPart title="Good to know" about="What a printed QR can and cannot do.">
          <div className="grid gap-2 sm:col-span-2">
            <Note tone="warning">
              The app cannot see the payment arrive. Check your phone or the bank’s message before
              you complete the sale.
            </Note>
            <Note>
              The customer types the amount in their own app. The checkout shows it large beside the
              QR.
            </Note>
          </div>
        </SettingsPart>
      </Card>

      <input
        ref={file}
        type="file"
        accept="image/*"
        hidden
        aria-label="A photo of your payment QR"
        onChange={(e) => {
          const picked = e.target.files?.[0]
          // The same photo can be chosen again after a failed read.
          e.target.value = ''
          void choose(picked)
        }}
      />
      {scanning && (
        <ScannerDialog
          mode="single"
          title="Scan your payment QR"
          onCode={(code) => {
            const read = take(code)
            return read.ok ? { ok: true, message: 'QR read' } : read
          }}
          onClose={() => setScanning(false)}
        />
      )}
      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={!draft?.fresh ? 'Payment QR details' : qr ? 'Replace payment QR' : 'Add payment QR'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button loading={busy} disabled={!draft?.label.trim()} onClick={() => void save()}>
              Save
            </Button>
          </>
        }
      >
        {draft && (
          <div className="grid gap-3.5">
            <div className="flex items-center gap-3.5">
              <PaymentQrTile payload={draft.payload} className="w-28" />
              {draft.fresh && (
                <div className="min-w-0">
                  <Badge tone="success" className="gap-1">
                    <CheckCircle2 className="size-3.5" aria-hidden />
                    QR read
                  </Badge>
                  <p className="mt-1.5 text-xs text-muted">
                    Drawn again from what your QR says. Scan this one with your own banking app to
                    check it opens your account.
                  </p>
                </div>
              )}
            </div>
            <Field label="Name" hint="Shown above the QR, like Fonepay or eSewa.">
              {(id) => (
                <Input
                  id={id}
                  value={draft.label}
                  maxLength={PAYMENT_QR_LABEL_MAX}
                  onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                />
              )}
            </Field>
            <Field
              label="Pays to"
              hint="Shown under the QR, so the customer can check who they are paying."
            >
              {(id) => (
                <Input
                  id={id}
                  value={draft.payee}
                  maxLength={PAYMENT_QR_PAYEE_MAX}
                  onChange={(e) => setDraft({ ...draft, payee: e.target.value })}
                />
              )}
            </Field>
            <Field label="Record these payments as" hint="How they show in payments and reports.">
              {(id) => (
                <Select
                  id={id}
                  value={draft.method}
                  onChange={(e) =>
                    setDraft({ ...draft, method: e.target.value as PaymentQrMethod })
                  }
                >
                  <option value="wallet">Wallet</option>
                  <option value="bank">Bank</option>
                </Select>
              )}
            </Field>
          </div>
        )}
      </Modal>
      <Modal
        open={problem !== null && draft === null}
        onClose={() => setProblem(null)}
        size="sm"
        title={qr ? 'Replace payment QR' : 'Add payment QR'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setProblem(null)}>
              Cancel
            </Button>
            {canScan ? (
              <Button
                icon={<Camera className="size-4" aria-hidden />}
                onClick={() => {
                  setProblem(null)
                  setScanning(true)
                }}
              >
                Scan with the camera
              </Button>
            ) : (
              <Button
                icon={<ImagePlus className="size-4" aria-hidden />}
                onClick={() => {
                  setProblem(null)
                  file.current?.click()
                }}
              >
                Choose another photo
              </Button>
            )}
          </>
        }
      >
        <div className="flex flex-col items-center gap-2 py-2 text-center" role="alert">
          <span className="flex size-12 items-center justify-center rounded-card bg-warning-light text-warning">
            <AlertTriangle className="size-6" aria-hidden />
          </span>
          <b>{problem?.title}</b>
          <p className="max-w-xs text-sm text-muted">{problem?.message}</p>
        </div>
      </Modal>
      <ConfirmDialog
        open={removing}
        title="Remove the payment QR?"
        message="The checkout stops showing it on every device. You can add it again at any time."
        confirmLabel="Remove"
        danger
        busy={busy}
        onClose={() => setRemoving(false)}
        onConfirm={() =>
          void commit(
            { payment_qr: null, payment_qr_label: null, payment_qr_payee: null },
            'Payment QR removed',
          ).then(() => setRemoving(false))
        }
      />
    </>
  )
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 font-semibold">{children}</dd>
    </div>
  )
}

function Locked() {
  return (
    <p className="mt-3 flex items-center gap-1.5 text-xs text-muted">
      <LockKeyhole className="size-4 shrink-0" aria-hidden />
      Only an owner or admin can change it.
    </p>
  )
}

function Who({
  icon: Icon,
  title,
  children,
}: {
  icon: IconType
  title: string
  children: ReactNode
}) {
  return (
    <div className="flex items-start gap-3">
      <IconTile area="neutral" size="sm">
        <Icon className="size-[17px]" />
      </IconTile>
      <div className="min-w-0">
        <b className="block text-sm">{title}</b>
        <span className="block text-xs text-muted">{children}</span>
      </div>
    </div>
  )
}

function Note({ tone, children }: { tone?: 'warning'; children: ReactNode }) {
  return (
    <p
      className={
        tone === 'warning'
          ? 'flex gap-2 rounded-xl bg-warning-light px-3 py-2 text-xs text-warning'
          : 'flex gap-2 rounded-xl bg-soft px-3 py-2 text-xs text-muted'
      }
    >
      <Info className="mt-px size-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  )
}
